import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { Phone, ArrowRight, CheckCircle2, MapPin, Star, ArrowLeft } from "lucide-react";
import { Button } from "./components/ui/button";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_custom-woodcraft-5/artifacts/lkf2d39x_530DA30B-6625-4EFA-99D8-BF45692B5D4C.png";

// Metro Vancouver areas (excluding Surrey for better leads)
const metroVancouverAreas = [
  "Vancouver",
  "North Vancouver", 
  "West Vancouver",
  "Burnaby",
  "Coquitlam",
  "Port Moody",
  "Richmond",
  "New Westminster"
];

// SEO Pages Data
const seoPageData = {
  "custom-built-ins-vancouver": {
    metaTitle: "Custom Built-Ins Vancouver | Wagner Carpentry & Millwork",
    metaDescription: "Premium custom built-ins in Vancouver & Metro Vancouver. Living room wall units, fireplace surrounds, home office storage. Owner-operated, detail-oriented craftsmanship.",
    h1: "Custom Built-Ins Vancouver",
    subtitle: "Premium Wall Units, Fireplace Surrounds & Home Office Storage",
    heroDescription: "Wagner Carpentry & Millwork creates stunning custom built-ins that transform your living spaces. From elegant living room wall units to functional home office storage, our owner-operated team delivers detail-oriented craftsmanship using only quality materials.",
    image: "https://customer-assets.emergentagent.com/job_custom-woodcraft-5/artifacts/3lc1qclh_0CBE5799-C3A6-4F58-BF9E-4DC49E85AA4B.png",
    services: [
      "Living Room Built-Ins & Wall Units",
      "Custom Fireplace Surrounds",
      "Home Office Storage Solutions",
      "Entertainment Centers",
      "Library & Bookcase Built-Ins",
      "Mudroom & Entryway Storage",
      "Custom Cabinetry & Millwork",
      "Bedroom Wardrobes & Closets"
    ],
    whyChoose: [
      "Owner-operated with hands-on attention to every project",
      "Detail-oriented craftsmanship you can see and feel",
      "Quality materials sourced from trusted suppliers",
      "Custom designs tailored to your exact specifications",
      "Transparent pricing with no hidden surprises",
      "Clean, professional job sites"
    ],
    relatedPages: [
      { url: "/feature-walls-vancouver", title: "Feature Walls Vancouver" },
      { url: "/finish-carpentry-vancouver", title: "Finish Carpentry Vancouver" },
      { url: "/services/custom-cabinetry", title: "Custom Cabinetry" }
    ],
    faq: [
      { q: "How much do custom built-ins cost in Vancouver?", a: "Custom built-in projects typically start around $1,500 for smaller installations. Larger wall units, fireplace surrounds, and full-room built-ins range from $5,000-$25,000+ depending on complexity, materials, and finishes. We provide detailed quotes after an in-home consultation." },
      { q: "How long does it take to build custom built-ins?", a: "Design and planning takes 1-2 weeks, fabrication 2-4 weeks, and installation 2-5 days depending on project scope. Most projects are completed within 4-6 weeks from approval." },
      { q: "What areas do you serve?", a: "We serve Metro Vancouver including Vancouver, North Vancouver, West Vancouver, Burnaby, Coquitlam, Port Moody, Richmond, and New Westminster." }
    ]
  },
  "feature-walls-vancouver": {
    metaTitle: "Custom Feature Walls Vancouver | Slat Walls & Accent Walls",
    metaDescription: "Modern feature walls & slat walls in Vancouver. Wood panel walls, TV feature walls, accent walls. Premium craftsmanship for Metro Vancouver homes.",
    h1: "Feature Walls Vancouver",
    subtitle: "Modern Slat Walls, Panel Walls & Accent Wall Installations",
    heroDescription: "Transform your space with a stunning custom feature wall from Wagner Carpentry & Millwork. Our wood slat walls, panel walls, and modern accent walls create dramatic focal points that elevate any room. Owner-operated quality for discerning Metro Vancouver homeowners.",
    image: "https://customer-assets.emergentagent.com/job_custom-woodcraft-5/artifacts/wc3omeng_IMG_3202.jpeg",
    services: [
      "Wood Slat Wall Installations",
      "Acoustic Panel Walls",
      "TV Feature Walls",
      "Modern Accent Walls",
      "Herringbone & Chevron Patterns",
      "Shiplap Feature Walls",
      "3D Textured Wall Panels",
      "Commercial Feature Walls"
    ],
    whyChoose: [
      "Specialists in modern, clean feature wall designs",
      "Premium materials including White Oak, Black Walnut & custom finishes",
      "Acoustic options for improved sound quality",
      "Owner-operated with meticulous attention to detail",
      "Seamless installation with minimal disruption",
      "Design consultation included"
    ],
    relatedPages: [
      { url: "/custom-built-ins-vancouver", title: "Custom Built-Ins Vancouver" },
      { url: "/finish-carpentry-vancouver", title: "Finish Carpentry Vancouver" },
      { url: "/services/accent-walls", title: "Accent Walls" }
    ],
    faq: [
      { q: "How much does a feature wall cost in Vancouver?", a: "Feature wall projects typically start around $1,500 for standard installations. Premium slat walls with acoustic backing and custom finishes range from $3,000-$10,000+ depending on wall size and materials. Contact us for a detailed quote." },
      { q: "What finishes are available for slat walls?", a: "We offer White Oak, Black Walnut, Matte Black, Natural Wood, and custom stain options. Acoustic panels are available with felt backing in various colors." },
      { q: "Do feature walls help with acoustics?", a: "Yes! Our acoustic slat panels with felt backing absorb echo and reverberation, making them perfect for living rooms, home theatres, and home offices." }
    ]
  },
  "finish-carpentry-vancouver": {
    metaTitle: "Finish Carpentry Vancouver | Trim, Millwork & Interior Finishing",
    metaDescription: "Expert finish carpentry in Vancouver & Metro Vancouver. Baseboards, crown molding, door & window trim. Premium interior finishing for renovations & new builds.",
    h1: "Finish Carpentry Vancouver",
    subtitle: "Baseboards, Crown Molding, Trim & Interior Millwork",
    heroDescription: "Wagner Carpentry & Millwork delivers premium finish carpentry services throughout Metro Vancouver. From elegant crown molding to precision baseboards and custom trim, our detail-oriented craftsmanship provides the perfect finishing touch for renovations and new builds.",
    image: "https://customer-assets.emergentagent.com/job_custom-woodcraft-5/artifacts/p3z1j895_IMG_3246.jpeg",
    services: [
      "Crown Molding Installation",
      "Baseboard Installation & Replacement",
      "Door & Window Casing",
      "Wainscoting & Wall Paneling",
      "Chair Rails & Picture Rails",
      "Coffered Ceilings",
      "Custom Trim Profiles",
      "Interior Millwork"
    ],
    whyChoose: [
      "Precision craftsmanship with seamless joints",
      "Experience with high-end renovations & new builds",
      "Trusted by designers and contractors",
      "Owner-operated quality control",
      "Clean, efficient installations",
      "Premium materials and finishes"
    ],
    relatedPages: [
      { url: "/custom-built-ins-vancouver", title: "Custom Built-Ins Vancouver" },
      { url: "/feature-walls-vancouver", title: "Feature Walls Vancouver" },
      { url: "/services/finishing-carpentry", title: "Finishing Carpentry" }
    ],
    faq: [
      { q: "How much does finish carpentry cost in Vancouver?", a: "Finish carpentry projects typically start around $1,500. Crown molding runs $8-15 per linear foot, baseboards $5-12 per linear foot depending on profile and material. Full-room trim packages and custom millwork are quoted based on scope." },
      { q: "Do you work with contractors and designers?", a: "Absolutely! We regularly partner with renovation contractors, interior designers, and builders throughout Metro Vancouver. We offer competitive trade pricing and reliable scheduling." },
      { q: "What's included in finish carpentry?", a: "Our finish carpentry services include all interior trim work: baseboards, crown molding, door casings, window trim, wainscoting, chair rails, and custom millwork profiles." }
    ]
  }
};

// Navigation Component
const Navigation = () => (
  <nav className="fixed top-0 left-0 right-0 z-50 bg-stone-50/95 backdrop-blur-sm border-b-2 border-stone-200">
    <div className="max-w-screen-2xl mx-auto px-3 md:px-6">
      <div className="flex items-center justify-between h-20">
        <Link to="/" className="mt-2">
          <img src={LOGO_URL} alt="Wagner Carpentry" className="h-[124px] w-auto" />
        </Link>
        <a
          href="tel:+16049064058"
          className="flex items-center gap-2 bg-stone-900 text-stone-50 border-2 border-stone-900 px-6 py-3 font-mono text-xs tracking-wider uppercase"
        >
          <Phone className="w-4 h-4" />
          604-906-4058
        </a>
      </div>
    </div>
  </nav>
);

// SEO Page Component
export const SeoServicePage = ({ slug }) => {
  const page = seoPageData[slug];
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  useEffect(() => {
    if (page) {
      document.title = page.metaTitle;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) metaDesc.setAttribute('content', page.metaDescription);
    }
    window.scrollTo(0, 0);
  }, [page, slug]);

  if (!page) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-heading text-4xl font-bold mb-4">Page Not Found</h1>
          <Link to="/" className="text-amber-700 hover:underline">Return Home</Link>
        </div>
      </div>
    );
  }

  const scrollToContact = () => {
    window.location.href = "/#contact";
  };

  return (
    <div className="font-body bg-stone-50">
      <Navigation />

      {/* Hero Section */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-24 border-b-2 border-stone-200">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12">
          <Link to="/" className="inline-flex items-center gap-2 font-mono text-xs tracking-wider uppercase text-stone-500 hover:text-stone-900 mb-8">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-stone-900 leading-[0.95] mb-6">
                {page.h1}
              </h1>
              <p className="font-mono text-sm tracking-wider uppercase text-amber-700 mb-6">
                {page.subtitle}
              </p>
              <p className="text-lg text-stone-600 leading-relaxed mb-8">
                {page.heroDescription}
              </p>
              
              {/* Trust Badges */}
              <div className="flex flex-wrap gap-4 mb-8">
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-stone-100 border border-stone-200 text-xs font-mono uppercase tracking-wider">
                  <CheckCircle2 className="w-4 h-4 text-amber-700" /> Owner-Operated
                </span>
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-stone-100 border border-stone-200 text-xs font-mono uppercase tracking-wider">
                  <Star className="w-4 h-4 text-amber-700" /> 5.0 Google Rating
                </span>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={scrollToContact}
                  className="bg-stone-900 text-stone-50 border-2 border-stone-900 rounded-none px-8 py-6 font-mono text-xs tracking-wider uppercase"
                >
                  Request a Quote <ArrowRight className="ml-3 w-4 h-4" />
                </Button>
                <a
                  href="tel:+16049064058"
                  className="inline-flex items-center justify-center gap-2 border-2 border-stone-300 rounded-none px-8 py-6 font-mono text-xs tracking-wider uppercase hover:border-stone-900 transition-colors"
                >
                  <Phone className="w-4 h-4" /> 604-906-4058
                </a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="border-2 border-stone-200 overflow-hidden">
                <img src={page.image} alt={page.h1} className="w-full aspect-[4/3] object-cover" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 md:py-24" ref={ref}>
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-stone-900 mb-8">
                Services Included
              </h2>
              <ul className="space-y-4">
                {page.services.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-amber-700 mt-0.5 flex-shrink-0" />
                    <span className="text-stone-700">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-stone-900 mb-8">
                Why Choose Wagner Carpentry
              </h2>
              <ul className="space-y-4">
                {page.whyChoose.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-amber-700 mt-0.5 flex-shrink-0" />
                    <span className="text-stone-700">{item}</span>
                  </li>
                ))}
              </ul>

              {/* Minimum Project Note */}
              <div className="mt-10 p-6 bg-amber-50 border-2 border-amber-200">
                <p className="font-heading text-lg font-semibold text-stone-900 mb-2">Project Minimums</p>
                <p className="text-stone-600">Our projects typically start around <strong>$1,500</strong>. This ensures we can deliver the quality craftsmanship and attention to detail our clients expect.</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Service Areas - Metro Vancouver Focus */}
      <section className="py-16 md:py-24 bg-stone-100 border-t-2 border-b-2 border-stone-200">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-stone-900 mb-4">
            Serving Metro Vancouver
          </h2>
          <p className="text-stone-600 mb-8 max-w-2xl">
            Wagner Carpentry & Millwork proudly serves homeowners, designers, and contractors throughout Metro Vancouver with premium {page.h1.toLowerCase()} services.
          </p>
          <div className="flex flex-wrap gap-3">
            {metroVancouverAreas.map((area, index) => (
              <span key={index} className="inline-flex items-center gap-2 px-5 py-3 bg-white border-2 border-stone-200 font-mono text-xs tracking-wider uppercase hover:border-stone-900 transition-colors">
                <MapPin className="w-3 h-3 text-amber-700" /> {area}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-stone-900 mb-12">
            Frequently Asked Questions
          </h2>
          <div className="space-y-8 max-w-3xl">
            {page.faq.map((item, index) => (
              <div key={index} className="border-b-2 border-stone-200 pb-8">
                <h3 className="font-heading text-xl font-semibold text-stone-900 mb-3">{item.q}</h3>
                <p className="text-stone-600 leading-relaxed">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related Services - Internal Linking */}
      <section className="py-16 md:py-24 bg-stone-100 border-t-2 border-stone-200">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-stone-900 mb-8">
            Related Services
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {page.relatedPages.map((related, index) => (
              <Link
                key={index}
                to={related.url}
                className="p-6 bg-white border-2 border-stone-200 hover:border-stone-900 transition-colors group"
              >
                <h3 className="font-heading text-lg font-semibold text-stone-900 group-hover:text-amber-700 transition-colors">
                  {related.title}
                </h3>
                <span className="inline-flex items-center gap-2 mt-4 font-mono text-xs tracking-wider uppercase text-stone-500 group-hover:text-stone-900">
                  Learn More <ArrowRight className="w-3 h-3" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-stone-900 text-stone-50">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-3xl md:text-5xl font-bold mb-6">
            Ready to Start Your Project?
          </h2>
          <p className="text-stone-400 text-lg mb-10 max-w-xl mx-auto">
            Contact Wagner Carpentry & Millwork for a free consultation. We serve homeowners, designers, and contractors throughout Metro Vancouver.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={scrollToContact}
              className="bg-stone-50 text-stone-900 border-2 border-stone-50 rounded-none px-8 py-6 font-mono text-xs tracking-wider uppercase hover:bg-amber-500 hover:border-amber-500 hover:text-white"
            >
              Request a Quote <ArrowRight className="ml-3 w-4 h-4" />
            </Button>
            <a
              href="tel:+16049064058"
              className="inline-flex items-center justify-center gap-2 border-2 border-stone-50 text-stone-50 rounded-none px-8 py-6 font-mono text-xs tracking-wider uppercase hover:bg-stone-50 hover:text-stone-900 transition-colors"
            >
              <Phone className="w-4 h-4" /> 604-906-4058
            </a>
          </div>
        </div>
      </section>

      {/* Google Reviews Badge */}
      <section className="py-12 bg-stone-800">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-amber-500 text-amber-500" />
                ))}
              </div>
              <span className="text-stone-300 font-mono text-sm">5.0 Rating on Google</span>
            </div>
            <a
              href="https://maps.app.goo.gl/aJ1qLzHyFuaoLEYt8"
              target="_blank"
              rel="noopener noreferrer"
              className="text-stone-400 hover:text-stone-50 font-mono text-xs tracking-wider uppercase transition-colors"
            >
              See Our Reviews →
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-stone-900 text-stone-50 py-12 border-t border-stone-800">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <img src={LOGO_URL} alt="Wagner Carpentry" className="h-[124px] w-auto brightness-0 invert" />
            <div className="text-center md:text-right">
              <p className="text-stone-400 font-mono text-xs tracking-wider mb-2">
                Serving Metro Vancouver: Vancouver, North Vancouver, West Vancouver, Burnaby, Coquitlam, Port Moody, Richmond
              </p>
              <p className="text-stone-500 font-mono text-xs tracking-wider">
                © {new Date().getFullYear()} Wagner Carpentry & Millwork
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SeoServicePage;
